import { useMemo } from "react";

import { Product } from "@/types";

import {
    useOrderProductSearch,
    getOpenSearchStoreStatusMessage,
} from "@/opensearch";

type OrderProductSearchStatus =
    | "loading"
    | "ready"
    | "missing_store_id"
    | "unavailable";

type UseSearchProductsOptions = {
    filter: string;
    outOfStock: boolean;
    providerStoreID?: string;
};

type UseSearchProductsResult = {
    products: Product[];
    isSearching: boolean;
    status: OrderProductSearchStatus;
    statusStoreId?: string;
    statusMessage: ReturnType<typeof getOpenSearchStoreStatusMessage>;
    isSearchEnabled: boolean;
};

const useSearchProducts = ({
    filter,
    outOfStock,
    providerStoreID,
}: UseSearchProductsOptions): UseSearchProductsResult => {
    const storeId = providerStoreID?.trim() ?? "";

    const {
        products,
        isSearching: isOpenSearchFetching,
        isLoading,
        readiness,
        isReady,
    } = useOrderProductSearch({
        providerStoreID,
        searchText: filter,
        showOutOfStock: outOfStock,
    });

    const status = useMemo((): OrderProductSearchStatus => {
        if (isLoading) {
            return "loading";
        }

        if (!storeId) {
            return "missing_store_id";
        }

        if (!isReady) {
            return "unavailable";
        }

        return "ready";
    }, [isLoading, storeId, isReady]);

    const statusMessage = useMemo(() => {
        if (status === "loading") {
            return "Loading store configuration…";
        }

        if (status === "missing_store_id") {
            return "This order has no provider store id. Product search is unavailable.";
        }

        if (status === "unavailable") {
            return getOpenSearchStoreStatusMessage(readiness);
        }

        return null;
    }, [status, readiness]);

    const isSearching =
        status === "loading" || (status === "ready" && isOpenSearchFetching);

    return {
        products: status === "ready" ? products : [],
        isSearching,
        status,
        statusStoreId: storeId || undefined,
        statusMessage,
        isSearchEnabled: status === "ready",
    };
};

export { useSearchProducts };
export type { OrderProductSearchStatus, UseSearchProductsResult };
